/**
 * Centralized configuration service for PDF image capture canvas elements
 * Provides project-specific configurations for element discovery and timing
 */

import { EProjectType } from '../enum/project';
import { ProjectCanvasConfig, TimingConfig, SpecialHandling } from '../types/ProjectCanvasConfig';
import canvasConfigData from '../data/project-canvas-config.json';

class ProjectConfigurationService {
    // Build Map from JSON data
    private static readonly configurations: Map<EProjectType, ProjectCanvasConfig> = new Map(
        Object.entries(canvasConfigData.projectConfigurations).map(([key, config]) => [
            EProjectType[key as keyof typeof EProjectType],
            {
                ...(config as any),
                projectType: EProjectType[key as keyof typeof EProjectType],
            } as ProjectCanvasConfig,
        ])
    );

    /**
     * Get configuration for a specific project type
     * @param projectType The project type to get configuration for
     * @returns Project canvas configuration
     * @throws Error if project type is not supported
     */
    public static getProjectConfig(projectType: EProjectType): ProjectCanvasConfig {
        const config = this.configurations.get(projectType);
        if (!config) {
            throw new Error(`Unsupported project type: ${projectType}. Supported types: ${Array.from(this.configurations.keys()).join(', ')}`);
        }
        return config;
    }

    /**
     * Check if a project type is supported
     * @param projectType The project type to check
     * @returns True if supported, false otherwise
     */
    public static isProjectTypeSupported(projectType: EProjectType): boolean {
        return this.configurations.has(projectType);
    }

    /**
     * Get all supported project types
     * @returns Array of supported project types
     */
    public static getSupportedProjectTypes(): EProjectType[] {
        return Array.from(this.configurations.keys());
    }

    /**
     * Get default timing configuration
     * @returns Default timing configuration for fallback scenarios
     */
    public static getDefaultTimingConfig(): TimingConfig {
        return canvasConfigData.fallbackConfigurations.defaultTiming as TimingConfig;
    }

    /**
     * Get enhanced timing for projects with complex canvas (geogrid/Konva)
     * @returns Enhanced timing configuration for complex rendering
     */
    public static getEnhancedTimingConfig(): TimingConfig {
        return canvasConfigData.fallbackConfigurations.enhancedTiming as TimingConfig;
    }

    /**
     * Validate a project canvas configuration
     * @param config The configuration to validate
     * @returns True if valid, false otherwise
     */
    public static validateConfig(config: ProjectCanvasConfig): boolean {
        if (!config.projectType || !config.canvasElements || !config.timingConfig || !config.specialHandling) {
            return false;
        }

        if (!config.canvasElements.primary || !config.canvasElements.secondary) {
            return false;
        }

        if (config.timingConfig.baseTimeout <= 0 || config.timingConfig.retryCount < 0 || config.timingConfig.canvasWaitTime < 0) {
            return false;
        }

        return true;
    }

    /**
     * Get dynamic canvas IDs for paved projects based on enabled design toggles
     * @param projectData The project data containing design toggle states
     * @returns Array of dynamic canvas element IDs to capture
     */
    public static getDynamicPavedCanvasIds(projectData?: any): string[] {
        const dynamicCanvasIds: string[] = [];

        if (!projectData) {
            return dynamicCanvasIds;
        }

        // Check surface design toggle
        if (projectData.surfaceDesign?.enableSurfaceDesign === 'yes') {
            dynamicCanvasIds.push('surface-design-canvas-1', 'surface-design-canvas-2', 'surface-design-canvas-3');
        }

        // Check base design toggle
        if (projectData.baseDesign?.enableBaseDesign === 'yes') {
            dynamicCanvasIds.push('base-design-canvas-1', 'base-design-canvas-2', 'base-design-canvas-3');
        }

        // Check subbase design toggle
        if (projectData.subbaseDesign?.enableSubbaseDesign === 'yes') {
            dynamicCanvasIds.push('subbase-design-canvas-1', 'subbase-design-canvas-2', 'subbase-design-canvas-3');
        }

        return dynamicCanvasIds;
    }
}

export default ProjectConfigurationService;
